import React, { useState, useEffect } from 'react';
import { getPendingCount, syncQueue } from '../lib/auditQueue';

// Sync status indicator — always visible in the app header
// Shows inspectors exactly how many audit events are waiting to upload
// so they know before leaving a site whether their records are fully saved.

const SyncStatus = () => {
  const [pending, setPending] = useState(getPendingCount());
  const [syncing, setSyncing] = useState(false);
  const [online, setOnline] = useState(navigator.onLine);

  useEffect(() => {
    const handleQueueUpdate = () => setPending(getPendingCount());
    const handleOnline = () => setOnline(true);
    const handleOffline = () => setOnline(false);

    window.addEventListener('auditQueueUpdate', handleQueueUpdate);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Poll pending count every 5s as a fallback
    // (queue updates fire events but this catches edge cases)
    const poll = setInterval(() => setPending(getPendingCount()), 5000);

    return () => {
      window.removeEventListener('auditQueueUpdate', handleQueueUpdate);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      clearInterval(poll);
    };
  }, []);

  const handleManualSync = async () => {
    if (syncing || !online) return;
    setSyncing(true);
    await syncQueue();
    setPending(getPendingCount());
    setSyncing(false);
  };

  // Determine status
  const isOffline = !online;
  const hasPending = pending > 0;

  // Colors and labels
  let bg, color, dot, label;

  if (isOffline && hasPending) {
    bg = '#7f1d1d'; color = '#fca5a5'; dot = '#ef4444';
    label = `Offline · ${pending} unsaved`;
  } else if (isOffline) {
    bg = '#374151'; color = '#9ca3af'; dot = '#6b7280';
    label = 'Offline';
  } else if (syncing) {
    bg = '#1e3a5f'; color = '#93c5fd'; dot = '#3b82f6';
    label = 'Syncing...';
  } else if (hasPending) {
    bg = '#78350f'; color = '#fcd34d'; dot = '#f59e0b';
    label = `${pending} pending`;
  } else {
    bg = '#14532d'; color = '#86efac'; dot = '#22c55e';
    label = 'All saved';
  }

  return (
    <button
      onClick={handleManualSync}
      disabled={syncing || isOffline || !hasPending}
      title={hasPending && online ? 'Tap to sync now' : label}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        padding: '4px 10px',
        borderRadius: '999px',
        background: bg,
        border: 'none',
        cursor: hasPending && online && !syncing ? 'pointer' : 'default',
        transition: 'all 0.2s ease',
        flexShrink: 0,
      }}
    >
      {/* Animated dot */}
      <span style={{
        width: 8,
        height: 8,
        borderRadius: '50%',
        background: dot,
        flexShrink: 0,
        animation: syncing ? 'pulse 1s ease-in-out infinite' : 'none',
      }} />
      <span style={{
        fontSize: '0.75rem',
        fontWeight: 600,
        color,
        whiteSpace: 'nowrap',
      }}>
        {label}
      </span>
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.3; }
        }
      `}</style>
    </button>
  );
};

export default SyncStatus;
